import { useMemo, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  TextField,
  Chip,
  Checkbox,
  ListItemText,
  Snackbar,
  CircularProgress,
} from '@mui/material'
import { Add, Delete as DeleteIcon, Gavel, AutoAwesome } from '@mui/icons-material'
import {
  useGetJudgeAssignmentsQuery,
  useCreateJudgeAssignmentMutation,
  useDeleteJudgeAssignmentMutation,
  useAutoAssignFromBracketMutation,
} from '../store/api/judgeAssignmentsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCaseRoundsQuery, RoundType } from '../store/api/roundsApi'
import { useGetUsersQuery } from '../store/api/usersApi'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'

// Vòng tranh tụng (sau bốc thăm) — auto-from-bracket chỉ áp dụng cho 2 vòng này
const HEARING_ROUND_TYPES = new Set<string>([RoundType.SEMIFINAL, RoundType.FINAL])

// Sentinel cho multi-select round: nghĩa là "Toàn case" — tạo 1 record round_id=null.
// Chọn cùng các round_id cụ thể khác sẽ bị tự động loại trừ (mutual exclusion).
const ALL_ROUNDS_SENTINEL = '__ALL__'

// Audit 2026-05-21: dùng helper utils/roundLabels — single source of truth.
const ROUND_TYPE_LABEL: Record<string, string> = {
  [RoundType.TRAINING]: 'Vòng Đào tạo',
  [RoundType.MEMORIAL]: 'Vòng Sơ loại',
  [RoundType.SEMIFINAL]: 'Vòng Bán kết',
  [RoundType.FINAL]: 'Vòng Chung kết',
}

const formatRoundLabel = (r: { name?: string | null; round_type?: string | null }) => {
  // Admin luôn đặt name có ý nghĩa cho round; type label chỉ là enum kỹ thuật và
  // hay trùng/đồng nghĩa với name → bỏ luôn type label để tránh duplicate hiển thị.
  // Fallback về type label khi name rỗng.
  const name = (r.name || '').trim()
  if (name) return name
  return ROUND_TYPE_LABEL[r.round_type as string] || r.round_type || ''
}

export default function JudgeAssignmentsPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage =
    !!user &&
    (hasPermission(user.role, 'ASSIGN_CASES') || hasPermission(user.role, 'MANAGE_USERS'))

  // Filter state cho danh sách (chia sẻ chọn case + round để dễ thao tác)
  const [filterCaseId, setFilterCaseId] = useState('')
  const [filterRoundId, setFilterRoundId] = useState('')

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: judges = [] } = useGetUsersQuery({ role: 'judge' }, { skip: !canManage })
  const { data: rounds = [] } = useGetCaseRoundsQuery(filterCaseId, { skip: !filterCaseId })

  const { data: rows = [], isLoading } = useGetJudgeAssignmentsQuery(
    canManage
      ? { case_id: filterCaseId || undefined, round_id: filterRoundId || undefined }
      : undefined,
    { skip: !canManage && user?.role !== 'judge' },
  )

  const [createAssignment, { isLoading: creating }] = useCreateJudgeAssignmentMutation()
  const [deleteAssignment] = useDeleteJudgeAssignmentMutation()
  const [autoAssign, { isLoading: autoAssigning }] = useAutoAssignFromBracketMutation()

  // Form state — Tab 0 (Tạo phân công thủ công)
  const [formJudgeId, setFormJudgeId] = useState('')
  const [formCaseId, setFormCaseId] = useState('')
  // Multi-select: [] = chưa chọn; [ALL_ROUNDS_SENTINEL] = "Toàn case" (1 record round_id=null);
  // ngược lại = mảng round_ids cụ thể (BE sẽ tạo N record, 1 record / round).
  const [formRoundIds, setFormRoundIds] = useState<string[]>([])
  const [formTeamIds, setFormTeamIds] = useState<string[]>([])
  const [formNotes, setFormNotes] = useState('')

  const { data: formRounds = [] } = useGetCaseRoundsQuery(formCaseId, { skip: !formCaseId })
  const formCaseTeams = useMemo(() => {
    const c = cases.find((x) => x.id === formCaseId)
    if (!c?.assigned_team_ids?.length) return []
    return teams.filter((t) => c.assigned_team_ids!.includes(t.id))
  }, [formCaseId, cases, teams])

  const isAllRounds = formRoundIds.includes(ALL_ROUNDS_SENTINEL)
  // Chỉ enforce "đúng 2 đội" khi tất cả round đã chọn đều là vòng tranh tụng.
  // Nếu mix (vd Memorial + Online_hearing) thì cho phép >2 đội — BE sẽ tạo record / round.
  const selectedFormRounds = formRounds.filter((r) => formRoundIds.includes(r.id))
  const allSelectedAreHearing =
    !isAllRounds &&
    selectedFormRounds.length > 0 &&
    selectedFormRounds.every((r) => HEARING_ROUND_TYPES.has(r.round_type as string))
  const anySelectedIsMemorial =
    !isAllRounds && selectedFormRounds.some((r) => r.round_type === RoundType.MEMORIAL)
  const teamHint = isAllRounds
    ? 'Áp dụng toàn case — tạo 1 phân công cho mọi vòng'
    : selectedFormRounds.length === 0
      ? 'Chọn ít nhất 1 vòng (hoặc "Toàn case")'
      : allSelectedAreHearing
        ? 'Vòng tranh tụng: chọn ĐÚNG 2 đội (1 cặp). Khuyến nghị dùng "Auto từ Bracket".'
        : anySelectedIsMemorial
          ? 'Vòng Sơ loại: GK có thể chấm nhiều đội độc lập'
          : 'Phân theo nhu cầu BTC'
  // isHearingRound giữ lại cho các đoạn code khác (auto-from-bracket): chỉ true khi
  // CHỈ chọn 1 vòng và đó là vòng tranh tụng.
  const isHearingRound = selectedFormRounds.length === 1 && allSelectedAreHearing
  // round_id duy nhất (cho auto-from-bracket — chỉ chạy khi user chọn 1 round)
  const formRoundId = selectedFormRounds.length === 1 ? selectedFormRounds[0].id : ''

  // Auto-from-bracket — dùng CHUNG case + round với form chính (chỉ thêm multi-GK riêng)
  const [autoJudgeIds, setAutoJudgeIds] = useState<string[]>([])

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const notify = (m: string, s: 'success' | 'error' | 'info' = 'info') =>
    setSnackbar({ open: true, message: m, severity: s })

  const handleCreate = async () => {
    if (!formJudgeId || !formCaseId || formTeamIds.length === 0) {
      notify('Cần đủ Giám khảo + Cuộc thi + ít nhất 1 đội', 'error')
      return
    }
    if (formRoundIds.length === 0) {
      notify('Chọn ít nhất 1 vòng thi (hoặc "Toàn case")', 'error')
      return
    }
    if (allSelectedAreHearing && formTeamIds.length !== 2) {
      notify('Vòng tranh tụng phải đúng 2 đội (1 cặp đấu)', 'error')
      return
    }
    // Build danh sách payload: ALL → 1 record round_id=null;
    // ngược lại → N record, mỗi record 1 round_id
    const payloads = isAllRounds
      ? [{ round_id: undefined as string | undefined }]
      : selectedFormRounds.map((r) => ({ round_id: r.id }))

    let okCount = 0
    const errors: string[] = []
    for (const p of payloads) {
      try {
        await createAssignment({
          judge_id: formJudgeId,
          case_id: formCaseId,
          round_id: p.round_id,
          team_ids: formTeamIds,
          notes: formNotes || undefined,
        }).unwrap()
        okCount += 1
      } catch (e: any) {
        errors.push(e?.data?.detail || e?.message || 'Lỗi không xác định')
      }
    }
    if (okCount === payloads.length) {
      notify(
        payloads.length === 1
          ? 'Đã tạo phân công'
          : `Đã tạo ${okCount} phân công cho ${payloads.length} vòng`,
        'success',
      )
      setFormRoundIds([])
      setFormTeamIds([])
      setFormNotes('')
    } else if (okCount > 0) {
      notify(
        `Tạo được ${okCount}/${payloads.length} phân công. Lỗi: ${errors.join('; ')}`,
        'error',
      )
    } else {
      notify(`Tạo phân công thất bại: ${errors.join('; ')}`, 'error')
    }
  }

  const handleAutoAssign = async () => {
    if (!formCaseId || !formRoundId) {
      notify('Chọn cuộc thi + vòng tranh tụng ở form trên', 'error')
      return
    }
    if (!isHearingRound) {
      notify('Auto từ bracket chỉ dùng cho vòng tranh tụng (Bán kết / Chung kết)', 'error')
      return
    }
    if (autoJudgeIds.length === 0) {
      notify('Chọn ít nhất 1 giám khảo', 'error')
      return
    }
    try {
      const result = await autoAssign({
        case_id: formCaseId,
        round_id: formRoundId,
        judge_ids: autoJudgeIds,
      }).unwrap()
      notify(`Đã sinh ${result.length} phân công từ bracket`, 'success')
      setAutoJudgeIds([])
      setFilterCaseId(formCaseId)
      setFilterRoundId(formRoundId)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Auto-assign thất bại', 'error')
    }
  }

  if (!user) return null

  if (!canManage && user.role !== 'judge') {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="error">Bạn không có quyền truy cập trang này.</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Phân công giám khảo"
        subtitle="Tách bạch theo vòng thi: Sơ loại (chấm hồ sơ, GK chấm nhiều đội độc lập) — Tranh tụng (theo cặp đấu sau bốc thăm)."
        icon={<Gavel />}
      />

      {canManage && (
        <Paper elevation={0} sx={(theme) => getFilterPaperSx(theme)}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Thêm phân công
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Vòng Sơ loại:</strong> 1 GK có thể chấm nhiều đội độc lập (round-robin). &nbsp;
            <strong>Vòng Tranh tụng:</strong> chọn đúng 2 đội cho 1 cặp đấu — hoặc dùng tab "Auto từ Bracket" để sinh tự động.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Giám khảo *</InputLabel>
              <Select
                label="Giám khảo *"
                value={formJudgeId}
                onChange={(e) => setFormJudgeId(e.target.value)}
              >
                {judges.map((j: any) => (
                  <MenuItem key={j.id} value={j.id}>
                    {j.full_name || j.username} ({j.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                label="Cuộc thi *"
                value={formCaseId}
                onChange={(e) => {
                  setFormCaseId(e.target.value)
                  setFormRoundIds([])
                  setFormTeamIds([])
                }}
              >
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!formCaseId}>
              <InputLabel>Vòng thi áp dụng *</InputLabel>
              <Select
                multiple
                label="Vòng thi áp dụng *"
                value={formRoundIds}
                onChange={(e) => {
                  const next = e.target.value as string[]
                  // Mutual exclusion giữa "Toàn case" và các round cụ thể.
                  // Nếu user vừa thêm sentinel → giữ duy nhất sentinel.
                  // Nếu user vừa thêm round_id cụ thể trong khi sentinel đang chọn → loại sentinel.
                  if (next.includes(ALL_ROUNDS_SENTINEL) && !formRoundIds.includes(ALL_ROUNDS_SENTINEL)) {
                    setFormRoundIds([ALL_ROUNDS_SENTINEL])
                  } else if (next.length > 1 && next.includes(ALL_ROUNDS_SENTINEL)) {
                    setFormRoundIds(next.filter((v) => v !== ALL_ROUNDS_SENTINEL))
                  } else {
                    setFormRoundIds(next)
                  }
                  setFormTeamIds([])
                }}
                renderValue={(sel) => {
                  const arr = sel as string[]
                  if (arr.includes(ALL_ROUNDS_SENTINEL)) return 'Toàn case (mọi vòng)'
                  return arr
                    .map((id) => {
                      const r = formRounds.find((x) => x.id === id)
                      return r ? formatRoundLabel(r) : id
                    })
                    .join(', ')
                }}
              >
                <MenuItem value={ALL_ROUNDS_SENTINEL}>
                  <Checkbox checked={isAllRounds} size="small" />
                  <ListItemText
                    primary={<em>— Toàn case (mọi vòng) —</em>}
                    secondary="1 phân công áp dụng cho mọi vòng (round_id = null)"
                  />
                </MenuItem>
                {/* Dedup theo ID — BE đôi khi trả trùng round nếu seed nhiều lần */}
                {Array.from(new Map(formRounds.map((r) => [r.id, r])).values()).map((r) => (
                  <MenuItem key={r.id} value={r.id} disabled={isAllRounds}>
                    <Checkbox checked={formRoundIds.includes(r.id)} size="small" />
                    <ListItemText primary={formatRoundLabel(r)} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Chọn nhiều vòng → BE sẽ tạo 1 phân công riêng cho mỗi vòng (cùng đội thi & ghi chú).
              </Typography>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!formCaseId}>
              <InputLabel>Đội thi *</InputLabel>
              <Select
                multiple
                label="Đội thi *"
                value={formTeamIds}
                onChange={(e) => setFormTeamIds(e.target.value as string[])}
                renderValue={(sel) =>
                  (sel as string[])
                    .map((id) => teams.find((t: any) => t.id === id)?.name || id)
                    .join(', ')
                }
              >
                {formCaseTeams.length === 0 && (
                  <MenuItem disabled>
                    <em>Cuộc thi chưa được phân đội nào</em>
                  </MenuItem>
                )}
                {formCaseTeams.map((t: any) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Checkbox checked={formTeamIds.includes(t.id)} size="small" />
                    <ListItemText primary={t.name} secondary={t.university} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                {teamHint}
                {allSelectedAreHearing && formTeamIds.length > 0 && formTeamIds.length !== 2 && (
                  <Typography component="span" color="error.main" sx={{ ml: 1 }}>
                    (đang chọn {formTeamIds.length} đội)
                  </Typography>
                )}
              </Typography>
            </FormControl>

            <TextField
              size="small"
              label="Ghi chú"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              fullWidth
            />

            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={
                creating ||
                !formJudgeId ||
                !formCaseId ||
                formRoundIds.length === 0 ||
                formTeamIds.length === 0 ||
                (allSelectedAreHearing && formTeamIds.length !== 2)
              }
              onClick={handleCreate}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {creating ? (
                <CircularProgress size={20} />
              ) : isAllRounds || formRoundIds.length <= 1 ? (
                'Lưu phân công'
              ) : (
                `Lưu ${formRoundIds.length} phân công (1/vòng)`
              )}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Contextual: chỉ hiện khi user chọn vòng tranh tụng → shortcut sinh hàng loạt từ bracket */}
      {canManage && isHearingRound && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            ...getFilterPaperSx(theme),
            mt: 2,
            borderColor: 'primary.main',
            borderStyle: 'dashed',
          })}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            <AutoAwesome fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tự sinh từ Bracket
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Vòng tranh tụng đã được bốc thăm? Chọn nhiều GK bên dưới — BE sẽ xóa phân công cũ
            cho cuộc thi & vòng đang chọn, rồi <strong>round-robin</strong> mỗi cặp đấu cho 1 GK.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Danh sách giám khảo *</InputLabel>
              <Select
                multiple
                label="Danh sách giám khảo *"
                value={autoJudgeIds}
                onChange={(e) => setAutoJudgeIds(e.target.value as string[])}
                renderValue={(sel) =>
                  (sel as string[])
                    .map((id) => {
                      // Audit 2026-05-21: KHÔNG fallback ra raw user_id.
                      const j: any = judges.find((j: any) => j.id === id)
                      return j?.full_name || j?.username || '(Đang tải...)'
                    })
                    .join(', ')
                }
              >
                {judges.map((j: any) => (
                  <MenuItem key={j.id} value={j.id}>
                    <Checkbox checked={autoJudgeIds.includes(j.id)} size="small" />
                    <ListItemText primary={j.full_name || j.username} secondary={j.email} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Số match nhiều hơn GK ⇒ có GK chấm nhiều cặp; ít hơn ⇒ có cặp rỗng (BTC bổ sung tay sau).
              </Typography>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AutoAwesome />}
              disabled={autoAssigning || autoJudgeIds.length === 0}
              onClick={handleAutoAssign}
              sx={{
                alignSelf: 'flex-start',
                textTransform: 'none',
                background: gradientBrand,
                '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
              }}
            >
              {autoAssigning ? <CircularProgress size={20} /> : 'Sinh phân công từ Bracket'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Filter list */}
      {canManage && (
        <Paper elevation={0} sx={(theme) => ({ ...getFilterPaperSx(theme), mt: 3 })}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Bộ lọc danh sách
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Cuộc thi</InputLabel>
              <Select
                label="Cuộc thi"
                value={filterCaseId}
                onChange={(e) => {
                  setFilterCaseId(e.target.value)
                  setFilterRoundId('')
                }}
              >
                <MenuItem value="">— Tất cả —</MenuItem>
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240 }} disabled={!filterCaseId}>
              <InputLabel>Vòng thi</InputLabel>
              <Select
                label="Vòng thi"
                value={filterRoundId}
                onChange={(e) => setFilterRoundId(e.target.value)}
              >
                <MenuItem value="">— Tất cả vòng —</MenuItem>
                {/* Chỉ hiển thị vòng của case đã chọn — dedup theo ID phòng BE trả trùng */}
                {Array.from(
                  new Map(
                    rounds
                      .filter((r) => !filterCaseId || r.case_id === filterCaseId)
                      .map((r) => [r.id, r]),
                  ).values(),
                ).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {formatRoundLabel(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper} elevation={0} sx={(theme) => ({ ...getTablePaperSx(theme), mt: 3 })}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cuộc thi</TableCell>
              <TableCell>Vòng</TableCell>
              <TableCell>Giám khảo</TableCell>
              <TableCell>Đội</TableCell>
              <TableCell>Ghi chú</TableCell>
              {canManage && <TableCell align="right">Thao tác</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={canManage ? 6 : 5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 6 : 5}>
                  <Typography color="text.secondary">Chưa có phân công.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const jc = cases.find((c: any) => c.id === r.case_id)
                const ju = judges.find((j: any) => j.id === r.judge_id)
                // Bỏ điều kiện `filterCaseId === r.case_id` — `rounds` là global query
                // nên luôn tìm được round name kể cả khi không filter case.
                const jr = r.round_id ? rounds.find((x) => x.id === r.round_id) : null
                return (
                  <TableRow key={r.id}>
                    <TableCell>{jc ? formatCaseDisplay(jc) : r.case_id}</TableCell>
                    <TableCell>
                      {r.round_id ? (
                        <Chip
                          size="small"
                          label={jr ? formatRoundLabel(jr) : 'Vòng đã xoá'}
                          color={
                            jr && HEARING_ROUND_TYPES.has(jr.round_type as string)
                              ? 'primary'
                              : 'default'
                          }
                          variant="outlined"
                        />
                      ) : (
                        <Chip size="small" label="Toàn case" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{(ju as any)?.full_name || (ju as any)?.username || '— Giám khảo chưa rõ —'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {r.team_ids.map((tid) => {
                          const t = teams.find((x: any) => x.id === tid)
                          return <Chip key={tid} size="small" label={(t as any)?.name || '— Đội chưa rõ —'} />
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>{r.notes || '—'}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (confirm('Xóa phân công này?')) {
                              deleteAssignment(r.id)
                                .unwrap()
                                .then(() => notify('Đã xóa', 'success'))
                                .catch((e: any) =>
                                  notify(e?.data?.detail || e?.message || 'Xóa thất bại', 'error'),
                                )
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
